import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * Modal for managing user assignments on a project
 * Allows PMs (if assigned to project) and admins to:
 * - Add/remove users from the project
 * - Change user role overrides for the project
 * 
 * Note: PMs cannot unassign admin or superadmin users - only admins can do that
 */
const ProjectUserAssignmentModal = React.memo(({ isOpen, onClose, projectId, projectName, onSaveSuccess, onSaveError }) => {
  const { instance, accounts } = useMsal();
  
  // Data state
  const [allUsers, setAllUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [accountingRoles, setAccountingRoles] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [currentUserIsAdmin, setCurrentUserIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  // UI state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [pendingChanges, setPendingChanges] = useState({
    toAdd: [], // { userId, roleOverride }
    toRemove: [], // userId
    roleChanges: {} // { [userId]: roleOverride }
  });
  
  // Admin roles that PMs cannot unassign
  const PROTECTED_ROLES = ['superadmin', 'admin', 'construction_owner', 'it_lead'];
  
  // Check if a user has a protected role (admin/superadmin)
  // V2: Use primary_role_id instead of roles array
  const isProtectedUser = (user) => {
    if (!user?.primary_role_id) return false;
    return PROTECTED_ROLES.includes(user.primary_role_id?.toLowerCase());
  };
  
  // Get the user's primary role name for display
  // V2: Use primary_role_id directly
  const getUserRoleName = (user) => {
    return user?.primary_role_id || user?.role_display_name || null;
  };

  // Fetch data when modal opens
  const fetchData = useCallback(async () => {
    if (!isOpen || !accounts || accounts.length === 0 || !projectId) return;
    
    setLoadingData(true);
    setError('');
    try {
      const [usersRes, assignedRes, rolesRes, permissionsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/project-assignments/assignable-users'),
        authenticatedApiCall(instance, accounts, 'GET', `/project-assignments/project/${projectId}/users`),
        authenticatedApiCall(instance, accounts, 'GET', '/project-assignments/accounting-roles'),
        authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info')
      ]);
      
      setAllUsers(usersRes.data.users || []);
      setAssignedUsers(assignedRes.data.data || []);
      setAccountingRoles(rolesRes.data.data || []);
      
      // Check if current user is admin/superadmin and get their ID
      const isAdmin = permissionsRes.data.data?.isAdmin || false;
      const userId = permissionsRes.data.data?.userId || null;
      setCurrentUserIsAdmin(isAdmin);
      setCurrentUserId(userId);
      
      setPendingChanges({ toAdd: [], toRemove: [], roleChanges: {} });
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load user data');
    } finally {
      setLoadingData(false);
    }
  }, [isOpen, instance, accounts, projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if a user is currently assigned (considering pending changes)
  const isUserAssigned = (userId) => {
    const originallyAssigned = assignedUsers.some(u => u.id === userId);
    const pendingAdd = pendingChanges.toAdd.some(a => a.userId === userId);
    const pendingRemove = pendingChanges.toRemove.includes(userId);
    
    if (pendingAdd) return true;
    if (pendingRemove) return false;
    return originallyAssigned;
  };

  // Get the current role override for a user (considering pending changes)
  const getUserRoleOverride = (userId) => {
    if (pendingChanges.roleChanges[userId] !== undefined) {
      return pendingChanges.roleChanges[userId];
    }
    const pendingAdd = pendingChanges.toAdd.find(a => a.userId === userId);
    if (pendingAdd) return pendingAdd.roleOverride || '';
    const assigned = assignedUsers.find(u => u.id === userId);
    return assigned?.role_override || '';
  };

  // Toggle user assignment
  const handleUserToggle = (userId) => {
    const user = allUsers.find(u => u.id === userId);
    const originallyAssigned = assignedUsers.some(u => u.id === userId);
    
    // Prevent users from removing themselves (use string comparison for type safety)
    if (originallyAssigned && currentUserId && String(userId) === String(currentUserId)) {
      setError('You cannot remove yourself from the project');
      return;
    }
    
    // Prevent PMs from unassigning admin/superadmin users
    if (originallyAssigned && !currentUserIsAdmin && user && isProtectedUser(user)) {
      setError('Only administrators can remove admin or superadmin users from projects');
      return;
    }
    
    // Clear any previous error
    setError('');
    
    setPendingChanges(prev => {
      const newChanges = { ...prev };
      
      if (originallyAssigned) {
        // User was originally assigned
        if (prev.toRemove.includes(userId)) {
          // Undo remove
          newChanges.toRemove = prev.toRemove.filter(id => id !== userId);
        } else {
          // Mark for removal
          newChanges.toRemove = [...prev.toRemove, userId];
          // Clear any role changes for this user
          const { [userId]: removed, ...restRoleChanges } = prev.roleChanges;
          newChanges.roleChanges = restRoleChanges;
        }
      } else {
        // User was not originally assigned
        const addIndex = prev.toAdd.findIndex(a => a.userId === userId);
        if (addIndex >= 0) {
          // Undo add
          newChanges.toAdd = prev.toAdd.filter(a => a.userId !== userId);
        } else {
          // Mark for addition
          newChanges.toAdd = [...prev.toAdd, { userId, roleOverride: null }];
        }
      }
      
      return newChanges;
    });
  };

  // Change role override for a user
  const handleRoleOverrideChange = (userId, roleOverride) => {
    const originallyAssigned = assignedUsers.some(u => u.id === userId);
    const pendingAddIndex = pendingChanges.toAdd.findIndex(a => a.userId === userId);
    
    if (pendingAddIndex >= 0) {
      // Update the pending add
      setPendingChanges(prev => ({
        ...prev,
        toAdd: prev.toAdd.map(a => 
          a.userId === userId ? { ...a, roleOverride: roleOverride || null } : a
        )
      }));
    } else if (originallyAssigned) {
      // Track role change for existing assignment
      setPendingChanges(prev => ({
        ...prev,
        roleChanges: { ...prev.roleChanges, [userId]: roleOverride || null }
      }));
    }
  };

  // Check if there are unsaved changes
  const hasChanges = () => {
    return pendingChanges.toAdd.length > 0 || 
           pendingChanges.toRemove.length > 0 || 
           Object.keys(pendingChanges.roleChanges).length > 0;
  };

  // Save all changes
  const handleSave = async () => {
    if (!hasChanges()) {
      onClose();
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      // Process removals
      for (const userId of pendingChanges.toRemove) {
        await authenticatedApiCall(
          instance,
          accounts,
          'DELETE',
          '/project-assignments/unassign',
          { userId, projectId }
        );
      }
      
      // Process additions
      for (const { userId, roleOverride } of pendingChanges.toAdd) {
        await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          '/project-assignments/assign',
          { userId, projectId, roleOverride }
        );
      }
      
      // Process role changes
      for (const [userId, roleOverride] of Object.entries(pendingChanges.roleChanges)) {
        await authenticatedApiCall(
          instance,
          accounts,
          'PUT',
          '/project-assignments/role-override',
          { userId, projectId, roleOverride }
        );
      }
      
      if (onSaveSuccess) {
        onSaveSuccess('Project team updated successfully');
      }
      onClose();
    } catch (err) {
      console.error('Error saving changes:', err);
      const errorMessage = err.response?.data?.message || 'Failed to save changes';
      setError(errorMessage);
      if (onSaveError) {
        onSaveError(errorMessage);
      }
    } finally {
      setSaving(false);
    }
  };

  // Filter users by search term
  const filteredUsers = allUsers.filter(user => {
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.display_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  // Sort users: assigned first, then by name
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aAssigned = isUserAssigned(a.id);
    const bAssigned = isUserAssigned(b.id);
    if (aAssigned && !bAssigned) return -1;
    if (!aAssigned && bAssigned) return 1;
    return (a.display_name || '').localeCompare(b.display_name || '');
  });

  const handleClose = () => {
    setPendingChanges({ toAdd: [], toRemove: [], roleChanges: {} });
    setUserSearchTerm('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modal: {
      backgroundColor: '#1a1a2e',
      borderRadius: '12px',
      padding: '0',
      width: '90%',
      maxWidth: '700px',
      maxHeight: '85vh',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: '1.5rem 2rem',
      borderBottom: '1px solid #2d2d44',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#16162a'
    },
    title: {
      margin: 0,
      color: '#e0e0e0',
      fontSize: '1.25rem',
      fontWeight: '600'
    },
    subtitle: {
      margin: '0.25rem 0 0 0',
      color: '#888',
      fontSize: '0.9rem'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#888',
      padding: '0.25rem',
      lineHeight: 1
    },
    content: {
      padding: '1.5rem 2rem',
      overflowY: 'auto',
      flex: 1
    },
    searchInput: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #3d3d5c',
      borderRadius: '6px',
      fontSize: '1rem',
      boxSizing: 'border-box',
      backgroundColor: '#242438',
      color: '#e0e0e0',
      marginBottom: '1rem'
    },
    userList: {
      maxHeight: '400px',
      overflowY: 'auto',
      border: '1px solid #3d3d5c',
      borderRadius: '6px',
      backgroundColor: '#1a1a2e'
    },
    userItem: (isSelected, isPending) => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #2d2d44',
      backgroundColor: isPending ? '#2a3a4f' : (isSelected ? '#1e3a5f' : 'transparent'),
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      gap: '1rem'
    }),
    userInfo: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: 0
    },
    checkbox: {
      marginRight: '0.75rem',
      width: '18px',
      height: '18px',
      accentColor: '#4a9eff'
    },
    userName: {
      color: '#e0e0e0',
      fontWeight: '500',
      marginRight: '0.75rem'
    },
    userEmail: {
      color: '#888',
      fontSize: '0.85rem'
    },
    roleSelect: {
      padding: '0.5rem',
      border: '1px solid #3d3d5c',
      borderRadius: '4px',
      fontSize: '0.85rem',
      backgroundColor: '#242438',
      color: '#e0e0e0',
      cursor: 'pointer',
      minWidth: '140px'
    },
    pendingBadge: {
      padding: '0.2rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.7rem',
      fontWeight: '600',
      marginLeft: '0.5rem'
    },
    addBadge: {
      backgroundColor: '#28a745',
      color: 'white'
    },
    removeBadge: {
      backgroundColor: '#dc3545',
      color: 'white'
    },
    changeBadge: {
      backgroundColor: '#ffc107',
      color: '#000'
    },
    error: {
      padding: '0.75rem 1rem',
      backgroundColor: 'rgba(220, 53, 69, 0.15)',
      color: '#f8d7da',
      borderRadius: '6px',
      marginBottom: '1rem',
      border: '1px solid rgba(220, 53, 69, 0.3)'
    },
    footer: {
      padding: '1.5rem 2rem',
      borderTop: '1px solid #2d2d44',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#16162a'
    },
    changesSummary: {
      color: '#888',
      fontSize: '0.85rem'
    },
    buttonGroup: {
      display: 'flex',
      gap: '0.75rem'
    },
    button: (variant) => ({
      padding: '0.75rem 1.5rem',
      backgroundColor: variant === 'primary' ? '#4a9eff' : variant === 'success' ? '#28a745' : '#3d3d5c',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    }),
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    assignedCount: {
      backgroundColor: '#4a9eff',
      color: 'white',
      padding: '0.25rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.8rem',
      marginLeft: '0.5rem'
    }
  };

  // Count current assignments (considering pending changes)
  const assignedCount = sortedUsers.filter(u => isUserAssigned(u.id)).length;
  const changesCount = pendingChanges.toAdd.length + pendingChanges.toRemove.length + Object.keys(pendingChanges.roleChanges).length;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>
              Manage Project Team
              <span style={styles.assignedCount}>{assignedCount} users</span>
            </h3>
            <p style={styles.subtitle}>{projectName}</p>
          </div>
          <button onClick={handleClose} style={styles.closeButton}>×</button>
        </div>

        <div style={styles.content}>
          {loadingData ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              Loading...
            </div>
          ) : (
            <>
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
              
              {error && <div style={styles.error}>{error}</div>}
              
              <div style={styles.userList}>
                {sortedUsers.length === 0 ? (
                  <p style={{ color: '#888', padding: '1rem', textAlign: 'center' }}>
                    {userSearchTerm ? 'No users found matching your search' : 'No users available'}
                  </p>
                ) : (
                  sortedUsers.map(user => {
                    const assigned = isUserAssigned(user.id);
                    const originallyAssigned = assignedUsers.some(u => u.id === user.id);
                    const isPendingAdd = pendingChanges.toAdd.some(a => a.userId === user.id);
                    const isPendingRemove = pendingChanges.toRemove.includes(user.id);
                    const hasRoleChange = pendingChanges.roleChanges[user.id] !== undefined;
                    const isPending = isPendingAdd || isPendingRemove || hasRoleChange;
                    
                    // Check if this user is protected (admin/superadmin) and current user is not admin
                    const isProtected = isProtectedUser(user);
                    // Use string comparison to handle type mismatches between number and string IDs
                    const isSelf = currentUserId && String(user.id) === String(currentUserId);
                    const cannotRemove = originallyAssigned && ((isProtected && !currentUserIsAdmin) || isSelf);
                    
                    return (
                      <div
                        key={user.id}
                        style={styles.userItem(assigned, isPending)}
                      >
                        <div 
                          style={{...styles.userInfo, cursor: cannotRemove ? 'not-allowed' : 'pointer'}} 
                          onClick={() => !cannotRemove && handleUserToggle(user.id)}
                          title={cannotRemove ? (isSelf ? 'You cannot remove yourself' : 'Only administrators can remove admin users') : ''}
                        >
                          <input
                            type="checkbox"
                            checked={assigned}
                            onChange={() => {}}
                            disabled={cannotRemove}
                            style={{
                              ...styles.checkbox,
                              opacity: cannotRemove ? 0.5 : 1,
                              cursor: cannotRemove ? 'not-allowed' : 'pointer'
                            }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <span style={styles.userName}>
                              {user.display_name}
                              {isSelf && (
                                <span style={{
                                  ...styles.pendingBadge,
                                  backgroundColor: '#17a2b8',
                                  color: 'white',
                                  marginLeft: '0.5rem'
                                }}>
                                  YOU
                                </span>
                              )}
                              {isProtected && (
                                <span style={{
                                  ...styles.pendingBadge,
                                  backgroundColor: '#6f42c1',
                                  color: 'white',
                                  marginLeft: '0.5rem'
                                }}>
                                  🔒 {getUserRoleName(user)}
                                </span>
                              )}
                              {isPendingAdd && <span style={{...styles.pendingBadge, ...styles.addBadge}}>NEW</span>}
                              {isPendingRemove && <span style={{...styles.pendingBadge, ...styles.removeBadge}}>REMOVE</span>}
                              {hasRoleChange && !isPendingAdd && <span style={{...styles.pendingBadge, ...styles.changeBadge}}>CHANGED</span>}
                            </span>
                            <span style={styles.userEmail}>{user.email}</span>
                          </div>
                        </div>
                        {assigned && !isPendingRemove && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <select
                              value={getUserRoleOverride(user.id) || ''}
                              onChange={(e) => handleRoleOverrideChange(user.id, e.target.value)}
                              style={styles.roleSelect}
                            >
                              <option value="">Default Role</option>
                              {accountingRoles.map(role => (
                                <option key={role.accounting_role} value={role.accounting_role}>
                                  {role.accounting_role} ({role.portal_role_name || 'No mapping'})
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        <div style={styles.footer}>
          <div style={styles.changesSummary}>
            {changesCount > 0 ? (
              <span style={{ color: '#ffc107' }}>
                {changesCount} pending change{changesCount !== 1 ? 's' : ''}
              </span>
            ) : (
              <span>No changes</span>
            )}
          </div>
          <div style={styles.buttonGroup}>
            <button
              onClick={handleClose}
              disabled={saving}
              style={{
                ...styles.button('secondary'),
                ...(saving ? styles.buttonDisabled : {})
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges()}
              style={{
                ...styles.button('success'),
                ...(saving || !hasChanges() ? styles.buttonDisabled : {})
              }}
            >
              {saving ? 'Saving...' : '✓ Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ProjectUserAssignmentModal;

